// ============================================================
// SecaoAtleta.js — Área do Atleta (só leitura da própria frequência)
//
// O atleta vê SÓ os próprios dados do mês atual. Lê os registros por
// userId e as dispensas que o afetam (as regras do Firestore garantem no
// servidor que ele não acessa dados de terceiros) e roda o MESMO
// calcularFrequencia de calculo.js — nenhuma lógica de cálculo duplicada.
//
// Blocos:
//  1. Banner com a mensagem da faixa (elogio ou alerta, conforme o nível).
//  2. Cartão com % grande + presenças / faltas / justificadas.
//  3. Datas especiais do mês (mesmo padrão da tela do técnico).
//  4. Solicitar correção de falta — campo visual (envio real vem no 3B,
//     com validação de prazo no servidor: do próprio dia do treino até
//     22h do 2º dia seguinte). Aqui o seletor já só
//     oferece datas dentro da janela de 48h.
//  5. Legenda das classificações + ações do programa.
// ============================================================

import { useState, useEffect } from "react";
import { carregarMeusRegistros, carregarMinhasDispensas, criarSolicitacao, carregarMinhasSolicitacoes, enviarComprovante, carregarMinhaPresencaFisica, carregarDiasTreinoFisico, auth } from "../firebase";
import { calcularFrequencia, normalizarModalidade } from "../calculo";
import { MESES, CINZA, CINZA_ESC, TEXTO, BRANCO, ALERTA_CL, PERIGO_CL, argbParaCss } from "../tema";
import { Card, Sel, Btn, StatusBox } from "../componentes";
import LegendaClassificacao, { MENSAGEM_POR_FAIXA } from "../componentes/LegendaClassificacao";
import RegistroPortal from "../componentes/RegistroPortal";
import ReconferirRosto from "../componentes/ReconferirRosto";
import GradePresencaFisicaDupla from "../componentes/GradePresencaFisicaDupla";
import useIsMobile from "../useIsMobile";
import { useNomeNucleo } from "../NucleosContexto";

export default function SecaoAtleta({ uid, userNucleo, modalidade, userNome }) {
  const nomeNucleo = useNomeNucleo();
  const isMobile = useIsMobile();
  const hoje = new Date();

  // Meses disponíveis pro atleta: o atual e o anterior. Resolve a "virada
  // de mês" — quando começa um mês novo e ele ainda não tem registro, pode
  // olhar o mês que fechou. Não abrimos o histórico todo de propósito
  // (mantém a tela simples e focada no acompanhamento recente).
  const mesesDisponiveis = (() => {
    const lista = [];
    for (let i = 0; i <= 1; i++) {
      const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
      lista.push({ mes: d.getMonth(), ano: d.getFullYear(), label: `${MESES[d.getMonth()]} de ${d.getFullYear()}`, chave: `${d.getFullYear()}-${d.getMonth()}` });
    }
    return lista;
  })();
  const [periodoSel, setPeriodoSel] = useState(mesesDisponiveis[0].chave);
  const periodo = mesesDisponiveis.find(p => p.chave === periodoSel) || mesesDisponiveis[0];
  const mesNum = periodo.mes;
  const anoNum = periodo.ano;

  const [carregando, setCarregando] = useState(true);
  const [status, setStatus] = useState(null);
  const [meu, setMeu] = useState(null); // resultado do próprio atleta
  const [dispensasMinhas, setDispensasMinhas] = useState([]);

  // Campo de solicitação (envio real via Cloud Function).
  const [dataFalta, setDataFalta] = useState("");
  const [justificativa, setJustificativa] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [statusEnvio, setStatusEnvio] = useState(null);
  const [minhasSolicitacoes, setMinhasSolicitacoes] = useState([]);
  // O registro cuja foto não conferiu, se houver. Vem da mesma consulta dos
  // registros do mês, então não custa leitura nova.
  const [registroAConferir, setRegistroAConferir] = useState(null);
  // Treino físico do próprio atleta: { "2026-08-14": "presente" | "falta" }
  const [meuFisico, setMeuFisico] = useState({});
  const [diasDoMes, setDiasDoMes] = useState([]);   // colunas da grade
  const [diasFisicoSemana, setDiasFisicoSemana] = useState([]);
  const [arquivo, setArquivo] = useState(null);

  const modalidadeNorm = normalizarModalidade(modalidade);

  useEffect(() => {
    let ativo = true;
    (async () => {
      setCarregando(true);
      try {
        const registros = await carregarMeusRegistros(uid);

        // Só o mais recente, e só dos últimos dias: convite para tirar
        // outra foto de um registro de três semanas atrás não ajuda
        // ninguém, e a coordenação já analisou.
        const limite = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
        const pendente = registros
          .filter((r) => r.rostoConferido === false && (r.dateKey || "") >= limite)
          .sort((a, b) => (b.dateKey || "").localeCompare(a.dateKey || ""))[0] || null;
        if (ativo) setRegistroAConferir(pendente);
        const todasDispensas = await carregarMinhasDispensas(userNucleo, modalidade, userNome);

        const registrosDoMes = registros.filter(r => {
          const dk = r.dateKey || (r.data ? r.data.split("/").reverse().join("-") : "");
          const [y, m] = (dk || "").split("-");
          return parseInt(y) === anoNum && parseInt(m) === mesNum + 1;
        });
        const dispensasDoMes = todasDispensas.filter(d => {
          const [y, m] = d.data.split("-");
          return parseInt(y) === anoNum && parseInt(m) === mesNum + 1;
        });

        // O mapa de nomes, com uma pessoa só: ele mesmo.
        //
        // O atleta não pode ler o cadastro do núcleo, e não precisa:
        // `userNome` já é o nome do cadastro dele. Sem isto, o cálculo o
        // identificava pelo nome gravado nos registros antigos, e a
        // dispensa escrita com o nome novo não casava — ele via a própria
        // falta sem o abono que a coordenação vê abonado.
        const meuEmail = (auth.currentUser && auth.currentUser.email) || "";
        const nomesAtuais = meuEmail && userNome
          ? { [meuEmail.trim().toLowerCase()]: { nome: userNome, ativo: true } }
          : null;

        const calc = calcularFrequencia({ registros: registrosDoMes, dispensas: dispensasDoMes, mes: mesNum, ano: anoNum, nomesAtuais });
        // Encontra o próprio atleta no resultado (pode não haver registro
        // ainda no mês — nesse caso o cálculo não gera a linha dele).
        const eu = calc.membros.find(m => m.grupo === "ATLETA");
        // diasVisiveis inclui o dia de hoje "aguardando"; é a mesma régua de
        // colunas que o técnico vê, para as duas telas não divergirem.
        if (ativo) setDiasDoMes(calc.diasVisiveis || calc.diasTreino || []);

        // Dispensas que afetam o atleta (coletivas + da modalidade dele +
        // individuais dele), pra mostrar como "datas especiais".
        const relevantes = dispensasDoMes.filter(d =>
          d.tipo === "COLETIVA"
          || (d.tipo === "MODALIDADE" && normalizarModalidade(d.modalidade) === modalidadeNorm)
          || (d.tipo === "INDIVIDUAL" && d.nome === userNome)
        ).sort((a, b) => a.data.localeCompare(b.data));

        if (ativo) {
          setMeu(eu || { semRegistro: true });
          setDispensasMinhas(relevantes);
        }
        // Carrega as solicitações do atleta (para mostrar o status).
        try {
          const sols = await carregarMinhasSolicitacoes(uid);
          if (ativo) setMinhasSolicitacoes(sols);
        } catch { /* silencioso — não bloqueia a tela */ }

        // Treino físico: só o que é do próprio atleta. A consulta já nasce
        // restrita porque a regra do Firestore recusa consulta que possa
        // devolver registro de outra pessoa.
        try {
          const mesPrefixo = `${anoNum}-${String(mesNum + 1).padStart(2, "0")}`;
          const email = (auth.currentUser && auth.currentUser.email) || "";
          const [fis, cfg] = await Promise.all([
            carregarMinhaPresencaFisica(userNucleo, uid, email, mesPrefixo),
            carregarDiasTreinoFisico(userNucleo),
          ]);
          if (ativo) {
            setMeuFisico(fis || {});
            setDiasFisicoSemana(cfg[normalizarModalidade(modalidade || "").toLowerCase()] || []);
          }
        } catch { /* silencioso — a frequência principal não depende disto */ }
      } catch (e) {
        if (ativo) setStatus({ tipo: "erro", msg: "Erro ao carregar sua frequência: " + (e.message || "") });
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => { ativo = false; };
  }, [uid, userNucleo, modalidade, modalidadeNorm, mesNum, anoNum, userNome]);

  // Datas válidas pra solicitar correção: dias de treino (seg–sex) dentro
  // do prazo — até 22h do 2º dia após o treino. A trava REAL é no servidor;
  // aqui é só conveniência de não oferecer datas fora do prazo.
  //
  // Começa em HOJE (i = 0), não no dia anterior. O atleta que já sabe que
  // não vai conseguir ir costuma avisar de manhã, antes do treino — e o
  // servidor sempre aceitou a data de hoje (ele só recusa data futura).
  // Quem barrava era esta lista, que só oferecia os dois dias anteriores.
  const datasPermitidas = (() => {
    const opts = [];
    for (let i = 0; i <= 2; i++) {
      const d = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - i);
      const dow = d.getDay();
      if (dow >= 1 && dow <= 5) {
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        const rotulo = d.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "2-digit" });
        opts.push({ value: iso, label: i === 0 ? `Hoje — ${rotulo}` : rotulo });
      }
    }
    return opts;
  })();

  // A data está encerrada para novas solicitações?
  //
  // Pendente e aprovada encerram; NEGADA não. Negar quer dizer "assim não,
  // corrija e mande de novo", e a tela precisa dizer o mesmo que o servidor:
  // um atleta enviou sem o atestado, foi negado, e ficou sem conseguir
  // reenviar justamente quando já sabia o que corrigir.
  const situacaoDaData = (iso) => {
    const doDia = minhasSolicitacoes.filter(s => s.dataTreino === iso);
    if (doDia.some(s => (s.status || "pendente") === "pendente")) return "em análise";
    if (doDia.some(s => s.status === "aprovada")) return "já aprovado";
    // Complemento pedido: a análise parou esperando VOCÊ, e não o contrário.
    if (doDia.some(s => s.status === "complemento")) return "falta complementar";
    if (doDia.some(s => s.status === "negada")) return "negado, pode reenviar";
    return null;
  };
  const jaSolicitou = iso => {
    const sit = situacaoDaData(iso);
    return sit === "em análise" || sit === "já aprovado";
  };

  async function enviarSolicitacao() {
    if (!dataFalta || justificativa.trim().length < 5) {
      setStatusEnvio({ tipo: "erro", msg: "Selecione o dia e escreva a justificativa (mínimo 5 caracteres)." });
      return;
    }
    setEnviando(true);
    setStatusEnvio(null);
    try {
      let documentoUrl = null, documentoCaminho = null;
      // Se anexou um arquivo, sobe primeiro para o Storage e pega a URL.
      if (arquivo) {
        setStatusEnvio({ tipo: "ok", msg: "Enviando comprovante..." });
        const up = await enviarComprovante({ uid, nucleo: userNucleo, arquivo });
        documentoUrl = up.url;
        documentoCaminho = up.caminho;
      }
      await criarSolicitacao({ dataTreino: dataFalta, justificativa: justificativa.trim(), documentoUrl, documentoCaminho });
      setStatusEnvio({ tipo: "ok", msg: "Solicitação enviada! O coordenador vai analisar." });
      setDataFalta("");
      setJustificativa("");
      setArquivo(null);
      const sols = await carregarMinhasSolicitacoes(uid);
      setMinhasSolicitacoes(sols);
    } catch (e) {
      const msg = e?.message || "Não foi possível enviar.";
      setStatusEnvio({ tipo: "erro", msg });
    } finally {
      setEnviando(false);
    }
  }

  if (carregando) {
    return <Card><div style={{ fontSize: 13, color: CINZA_ESC }}>Carregando sua frequência de {MESES[mesNum]}...</div></Card>;
  }

  const semRegistro = !meu || meu.semRegistro;
  const classif = semRegistro ? null : meu.classif;
  const cor = classif ? argbParaCss(classif.cor) : CINZA_ESC;
  const bg = classif ? argbParaCss(classif.bg) : CINZA;
  const msg = classif ? MENSAGEM_POR_FAIXA[classif.label] : null;
  const msgEstilo = msg?.tom === "ok"
    ? { bg: "#D1FAE5", cor: "#047857" }
    : msg?.tom === "alerta"
      ? { bg: ALERTA_CL, cor: "#92400E" }
      : { bg: PERIGO_CL, cor: "#991B1B" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {status && <StatusBox tipo={status.tipo} msg={status.msg} />}

      {/* Mês — atual ou anterior */}
      <Card>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 12, color: CINZA_ESC }}>Sua frequência em</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: TEXTO }}>{periodo.label}</div>
            <div style={{ fontSize: 12.5, color: CINZA_ESC, marginTop: 2 }}>{modalidadeNorm} · Núcleo {nomeNucleo(userNucleo)}</div>
          </div>
          <div style={{ minWidth: 180 }}>
            <Sel label="Mês" value={periodoSel} onChange={setPeriodoSel}
              options={mesesDisponiveis.map(p => ({ value: p.chave, label: p.label }))} />
          </div>
        </div>
      </Card>

      {/* Registro de presença pelo portal (iOS / sem app) */}
      <RegistroPortal uid={uid} userNucleo={userNucleo} />

      {/* A presença já está registrada e vale. Isto é a chance de corrigir
          uma foto ruim antes de a coordenação precisar analisar. */}
      {registroAConferir && (
        <ReconferirRosto
          registro={registroAConferir}
          aoResolver={() => setRegistroAConferir(null)}
        />
      )}

      {semRegistro ? (
        <Card>
          <div style={{ fontSize: 14, color: TEXTO, fontWeight: 700, marginBottom: 4 }}>Ainda sem registros neste mês</div>
          <div style={{ fontSize: 13, color: CINZA_ESC, lineHeight: 1.6 }}>
            Assim que você registrar presença nos treinos pelo aplicativo, sua frequência aparece aqui.
          </div>
        </Card>
      ) : (
        <>
          {/* 1. Banner da faixa */}
          {msg && (
            <div style={{ background: msgEstilo.bg, borderRadius: 12, padding: "16px 18px", color: msgEstilo.cor, fontSize: 14, fontWeight: 600, lineHeight: 1.55 }}>
              {msg.texto}
            </div>
          )}

          {/* 2. Cartão principal */}
          <Card style={{ border: `1.5px solid ${cor}44`, background: bg }}>
            <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
              <div style={{ textAlign: "center", minWidth: 120 }}>
                <div style={{ fontSize: 44, fontWeight: 800, color: cor, lineHeight: 1 }}>{Math.round(meu.pctTotal * 100)}%</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: cor, marginTop: 6, padding: "3px 12px", background: BRANCO, borderRadius: 20, display: "inline-block" }}>
                  {classif.label}
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 200, display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)", gap: 10 }}>
                <div style={{ background: BRANCO, borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: TEXTO }}>{meu.presEfetiva}</div>
                  <div style={{ fontSize: 11, color: CINZA_ESC }}>Presenças</div>
                </div>
                <div style={{ background: BRANCO, borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: TEXTO }}>{meu.jc + meu.jm + meu.ji}</div>
                  <div style={{ fontSize: 11, color: CINZA_ESC }}>Faltas justificadas</div>
                </div>
                <div style={{ background: BRANCO, borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: TEXTO }}>{meu.faltas}</div>
                  <div style={{ fontSize: 11, color: CINZA_ESC }}>Faltas não justificadas</div>
                </div>
              </div>
            </div>
          </Card>
        </>
      )}

        {/* Grade de presença do próprio atleta — a MESMA que o técnico vê,
          com uma linha só: a dele. Treino diário em cima, treino físico
          embaixo.

          Antes esta tela mostrava só os totais ("18 presenças, 3 faltas"),
          e o atleta não sabia EM QUE DIA faltou — justo a informação de que
          ele precisa para usar o botão de justificar logo abaixo.

          Segurança: os dois conjuntos de dados já chegam restritos ao
          próprio atleta (registros por userId, treino físico por uid/e-mail),
          e as regras do Firestore recusam qualquer consulta mais ampla. */}
      {!semRegistro && diasDoMes.length > 0 && (
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEXTO, marginBottom: 2 }}>
            Seu dia a dia no mês
          </div>
          <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 10, lineHeight: 1.5 }}>
            A linha <b>Técnico</b> é o treino diário registrado no App, que conta na sua
            frequência. A linha <b>Físico</b> é o treino de preparação, marcado
            pelo preparador(a), e não entra no seu percentual, porém é prerrogativa
            obrigatória para executar o treino técnico.
          </div>
          {(() => {
            const modLower = normalizarModalidade(modalidade || "").toLowerCase();
            const mapaTecnico = {};
            (meu.mapaDiario || []).forEach(d => { mapaTecnico[d.dia] = d; });

            // A grade indexa o treino físico por "chave__dia". Como aqui há
            // um atleta só, a chave é fixa.
            const CHAVE = "eu";
            const presencaFisica = {};
            Object.entries(meuFisico).forEach(([dia, st]) => {
              presencaFisica[`${CHAVE}__${dia}`] = st;
            });

            return (
              <GradePresencaFisicaDupla
                atletas={[{
                  nome: userNome || "Você",
                  modalidade: modLower,
                  mapaTecnico,
                  pctTec: meu.pctTotal || 0,
                }]}
                diasTreino={diasDoMes}
                diasFisicoSemana={{ [modLower]: diasFisicoSemana }}
                presencaFisica={presencaFisica}
                chaveDe={() => CHAVE}
              />
            );
          })()}
        </Card>
      )}

      {/* 3. Datas especiais do mês */}
      {dispensasMinhas.length > 0 && (
        <Card>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Datas especiais do mês</div>
          <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 12 }}>
            Dias que afetam sua frequência: feriados e suspensões, dispensas da sua modalidade e justificativas suas.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {dispensasMinhas.map(d => {
              const ehComp = d.tipo === "MODALIDADE" && (d.subtipo || "").toLowerCase().includes("competi");
              const badge = d.tipo === "COLETIVA"
                ? { texto: "Sem treino", bg: "#EDE9FE", cor: "#7C3AED" }
                : ehComp
                  ? { texto: "Conta como presença", bg: "#D1FAE5", cor: "#047857" }
                  : { texto: "Falta justificada", bg: "#FEF3C7", cor: "#92400E" };
              return (
                <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, padding: "6px 0", borderBottom: "1px solid #F1F5F9", flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700, color: TEXTO, minWidth: 48 }}>
                    {new Date(d.data + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: badge.bg, color: badge.cor, whiteSpace: "nowrap" }}>{badge.texto}</span>
                  <span style={{ color: CINZA_ESC }}>{d.subtipo}{d.motivo ? ` — ${d.motivo}` : ""}</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* 4. Solicitar correção de falta */}
      <Card>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Solicitar correção de falta</div>
        <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 12, lineHeight: 1.6 }}>
          Já sabe que não vai conseguir ir, ou houve algum problema no dia do treino?
          Você pode justificar <b>no próprio dia</b>, inclusive antes do treino, e ainda
          em até <b>dois dias</b> depois. Passado esse prazo, a falta é confirmada.
        </div>

        {statusEnvio && <div style={{ marginBottom: 12 }}><StatusBox tipo={statusEnvio.tipo} msg={statusEnvio.msg} /></div>}

        {datasPermitidas.length === 0 ? (
          <div style={{ padding: "10px 12px", background: CINZA, borderRadius: 8, fontSize: 13, color: CINZA_ESC }}>
            Nenhum dia de treino dentro do prazo no momento.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Sel label="Dia do treino" value={dataFalta} onChange={setDataFalta}
              options={[{ value: "", label: "— selecione o dia —" },
                ...datasPermitidas.map(o => {
                  const sit = situacaoDaData(o.value);
                  return sit ? { ...o, label: o.label + " (" + sit + ")" } : o;
                })]} />
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: CINZA_ESC, display: "block", marginBottom: 5 }}>Justificativa</label>
              <textarea value={justificativa} onChange={e => setJustificativa(e.target.value)}
                placeholder="Explique o que aconteceu no dia do treino..."
                rows={3}
                style={{ width: "100%", border: "1px solid #D1D5DB", borderRadius: 8, padding: "9px 12px", fontSize: 14, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: CINZA_ESC, display: "block", marginBottom: 5 }}>Comprovante (opcional)</label>
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={e => setArquivo(e.target.files?.[0] || null)}
                style={{ fontSize: 13, color: CINZA_ESC }}
              />
              {arquivo && (
                <div style={{ fontSize: 12, color: CINZA_ESC, marginTop: 4 }}>
                  📎 {arquivo.name} ({(arquivo.size / 1024 / 1024).toFixed(1)}MB)
                  <button onClick={() => setArquivo(null)} style={{ marginLeft: 8, background: "none", border: "none", color: "#DC2626", cursor: "pointer", fontSize: 12 }}>remover</button>
                </div>
              )}
              <div style={{ fontSize: 11, color: CINZA_ESC, marginTop: 3 }}>Imagem ou PDF, até 5MB. Ex: atestado, declaração.</div>
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <Btn onClick={enviarSolicitacao} disabled={enviando || !dataFalta || jaSolicitou(dataFalta)}>
                {enviando ? "Enviando…" : "Enviar solicitação"}
              </Btn>
            </div>
            <div style={{ fontSize: 11.5, color: CINZA_ESC }}>
              Sua solicitação vai direto para o coordenador do núcleo analisar.
            </div>
          </div>
        )}

        {/* Solicitações já enviadas + status */}
        {minhasSolicitacoes.length > 0 && (
          <div style={{ marginTop: 18, borderTop: "1px solid #F1F5F9", paddingTop: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: TEXTO, marginBottom: 8 }}>Suas solicitações</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {minhasSolicitacoes.map(s => {
                const st = s.status === "aprovada"
                  ? { txt: "Aprovada", bg: "#D1FAE5", cor: "#047857" }
                  : s.status === "negada"
                    ? { txt: "Negada", bg: PERIGO_CL, cor: "#991B1B" }
                    : s.status === "complemento"
                      ? { txt: "Falta complementar", bg: "#DBEAFE", cor: "#1E40AF" }
                      : { txt: "Pendente", bg: ALERTA_CL, cor: "#92400E" };
                return (
                  <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, padding: "6px 0", flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700, color: TEXTO, minWidth: 48 }}>
                      {new Date(s.dataTreino + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: st.bg, color: st.cor }}>{st.txt}</span>
                    <span style={{ color: CINZA_ESC, flex: 1, minWidth: 140 }}>{s.justificativa}</span>
                    {s.documentoUrl && <a href={s.documentoUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: "#0891B2", fontWeight: 700 }}>📎 comprovante</a>}
                    {s.respostaCoord && <span style={{ color: CINZA_ESC, fontStyle: "italic", fontSize: 12 }}>— {s.respostaCoord}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      {/* 5. Legenda + ações do programa */}
      <Card>
        <LegendaClassificacao comAcoes />
      </Card>
    </div>
  );
}